import { tryParseQuery } from './parser'
import { isCustomFieldQueryKey } from './task-fields'
import {
  ParsedQuery,
  QueryExpressionNode,
  QueryFieldDefinition,
  QueryPosition,
} from './types'

export interface QueryValidationIssue {
  message: string
  position: QueryPosition | null
}

export interface QueryValidationResult {
  valid: boolean
  query: ParsedQuery | null
  issues: QueryValidationIssue[]
}

function findField(
  fields: QueryFieldDefinition[],
  name: string,
): QueryFieldDefinition | undefined {
  const lower = name.toLowerCase()
  return fields.find(
    field =>
      field.key.toLowerCase() === lower ||
      field.aliases?.some(alias => alias.toLowerCase() === lower),
  )
}

function collectComparisonIssues(
  node: QueryExpressionNode,
  fields: QueryFieldDefinition[],
  issues: QueryValidationIssue[],
): void {
  switch (node.kind) {
    case 'and':
    case 'or':
      node.children.forEach(child =>
        collectComparisonIssues(child, fields, issues),
      )
      return
    case 'not':
      collectComparisonIssues(node.child, fields, issues)
      return
    case 'comparison': {
      // Custom fields are resolved dynamically server-side
      if (isCustomFieldQueryKey(node.field)) return

      const field = findField(fields, node.field)
      if (!field) {
        issues.push({
          message: `Unknown field '${node.field}'`,
          position: node.position,
        })
        return
      }
      if (!field.operators.includes(node.operator)) {
        issues.push({
          message: `Operator '${node.operator}' is not supported for '${field.key}' (supported: ${field.operators.join(', ')})`,
          position: node.position,
        })
      }
    }
  }
}

/**
 * Parse and semantically validate a query against a module's field registry.
 * Both parse errors and unknown-field / bad-operator issues are reported.
 */
export function validateQuery(
  input: string,
  fields: QueryFieldDefinition[],
): QueryValidationResult {
  const trimmed = input.trim()
  if (!trimmed) {
    return { valid: true, query: { where: null, orderBy: [] }, issues: [] }
  }

  const { query, error } = tryParseQuery(trimmed)
  if (error || !query) {
    return {
      valid: false,
      query: null,
      issues: [
        {
          message: error?.message ?? 'Invalid query',
          position: error?.position ?? null,
        },
      ],
    }
  }

  const issues: QueryValidationIssue[] = []
  if (query.where) {
    collectComparisonIssues(query.where, fields, issues)
  }
  for (const item of query.orderBy) {
    const field = findField(fields, item.field)
    if (!field) {
      issues.push({
        message: `Unknown field '${item.field}' in ORDER BY`,
        position: null,
      })
    } else if (!field.sortable) {
      issues.push({
        message: `'${field.key}' cannot be used in ORDER BY`,
        position: null,
      })
    }
  }

  return { valid: issues.length === 0, query, issues }
}
